import { html, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { property, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';

import '~/ui/components/button';
import '~/ui/components/checkbox';
import '~/ui/components/input';
import '~/ui/components/module-title';
import '~/ui/components/phone-input';
import '~/ui/components/radio';
import '~/ui/components/radio-group';
import '~/ui/components/textarea';
import '~/ui/objects/module/body';
import '~/ui/objects/module/module';
import '~/ui/objects/module/optional';
import '~/ui/objects/module/title';

import { formatRichText } from '~/modules/utils/rich-text.helper';
import DERFormElement from '~/ui/utils/der-form-element';
import { AppointmentBookingFormTranslations } from '../appointment-booking.interface';

import { styles as globalStyles } from '~/ui/scss/main.styles';
import { styles } from './request-form.styles';

export type Variant = 'default' | 'costs';

export class RequestForm extends DERFormElement {
    static override styles = [unsafeCSS(globalStyles), unsafeCSS(styles)];

    private readonly emailPattern: string = '^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$';

    private readonly namePattern: string = "^(?!\\s+$)[A-Za-zÀ-ÖØ-öø-ÿ'\\- ]+$";

    @property({ type: Object })
    translations: AppointmentBookingFormTranslations = {} as AppointmentBookingFormTranslations;

    @property({ type: String })
    variant: Variant = 'default';

    @query('form') formElement?: HTMLFormElement;

    constructor() {
        super();
        this.attachShadow({
            mode: 'open',
            delegatesFocus: true,
        });
    }

    getFormValue(): Record<string, FormDataEntryValue> {
        return Object.fromEntries(new FormData(this.formElement));
    }

    onSubmit(e: Event): void {
        e.preventDefault();
        this.emitEvent('der.submit', this.getFormValue());
    }

    validate(): boolean {
        return this.formElement!.reportValidity();
    }

    private _checkFormValidity(): void {
        if (!this.formElement) {
            return;
        }
        const isValid = this.formElement?.checkValidity();
        this.setDERValidity({ badInput: !isValid }, 'Booking Appointment Form Validation', this.formElement);
    }

    override render() {
        return html`
            <div class="m-appointment-request">
                <form @der.validity-change=${this._checkFormValidity} @der.submit=${this.onSubmit}>
                    <div class="m-appointment-request__form">
                        <div class="m-appointment-request__inputs-wrapper">
                            <div class="m-appointment-request__input-wrapper">
                                <der-ui-radio-group
                                    id="genderGroup"
                                    name="ContactGender"
                                    label="${ifDefined(this.translations?.salutation)}"
                                    language="${this.lang}"
                                    ?required="${true}"
                                >
                                    <der-ui-radio-button
                                        value="2"
                                        label="${ifDefined(this.translations?.genders.female)}"
                                    ></der-ui-radio-button>
                                    <der-ui-radio-button
                                        value="1"
                                        label="${ifDefined(this.translations?.genders.male)}"
                                    ></der-ui-radio-button>
                                    <der-ui-radio-button
                                        value="3"
                                        label="${ifDefined(this.translations?.genders.other)}"
                                    ></der-ui-radio-button>
                                </der-ui-radio-group>
                            </div>
                            <div
                                class="m-appointment-request__input-wrapper m-appointment-request__multiple-inputs-wrapper"
                            >
                                <der-ui-input
                                    label="${ifDefined(this.translations?.fields.firstName)}"
                                    name="ContactFirstName"
                                    ?required="${true}"
                                    pattern="${this.namePattern}"
                                ></der-ui-input>
                                <der-ui-input
                                    label="${ifDefined(this.translations?.fields.lastName)}"
                                    name="ContactLastName"
                                    ?required="${true}"
                                    pattern="${this.namePattern}"
                                ></der-ui-input>
                            </div>
                            <div
                                class="m-appointment-request__input-wrapper m-appointment-request__multiple-inputs-wrapper"
                            >
                                <der-ui-phone-input
                                    lang="${this.lang}"
                                    label="${ifDefined(this.translations?.fields.phone)}"
                                    name="ContactPhone"
                                    input-placeholder=""
                                    ?required="${true}"
                                ></der-ui-phone-input>
                                <der-ui-input
                                    label="${ifDefined(this.translations?.fields.email)}"
                                    name="ContactEmail"
                                    inputmode="email"
                                    pattern="${this.emailPattern}"
                                    ?required="${true}"
                                    type="email"
                                ></der-ui-input>
                            </div>
                            <div class="m-appointment-request__email-confirmation">
                                <label for="emailConfirmation">eMail Confirmation</label>
                                <input
                                    type="email"
                                    id="emailConfirmation"
                                    name="ContactEmailConfirmation"
                                    tabindex="-1"
                                    autocomplete="new-password"
                                />
                            </div>
                            <div class="m-appointment-request__description">
                                <der-ui-textarea
                                    label="${ifDefined(this.translations?.fields.description)}"
                                    name="OpportunityDescription"
                                    ?required="${true}"
                                    maxlength="3000"
                                ></der-ui-textarea>
                            </div>
                            <div class="m-appointment-request__input-wrapper">
                                <der-ui-radio-group
                                    name="OpportunityPreferredMethodOfContact"
                                    label="${ifDefined(this.translations?.methodOfContactLabel)}"
                                    language="${this.lang}"
                                    ?required="${true}"
                                >
                                    <der-ui-radio-button
                                        value="Email"
                                        label="${ifDefined(this.translations?.methodOfContact.email)}"
                                    ></der-ui-radio-button>
                                    <der-ui-radio-button
                                        value="Phone"
                                        label="${ifDefined(this.translations?.methodOfContact.phone)}"
                                    ></der-ui-radio-button>
                                </der-ui-radio-group>
                            </div>
                            ${this.variant === 'costs'
                                ? html`
                                      <div class="m-appointment-request__checkbox-wrapper">
                                          <der-ui-checkbox name="OpportunityCostConfirmation" ?required="${true}">
                                          </der-ui-checkbox>
                                          <div class="m-appointment-request__checkbox-wrapper__text">
                                              ${ifDefined(this.translations?.opportunityCostConfirmation)}
                                          </div>
                                      </div>
                                  `
                                : ''}

                            <div class="m-appointment-request__data-protection-wrapper-wrapper">
                                ${unsafeHTML(formatRichText(this.translations?.requestDataProtectionLink || ''))}
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        `;
    }
}
